// Persistent license storage utilities with LRU eviction for quota management

export interface PersistentSessionMetadata {
  sessionId: string;
  initData: ArrayBuffer;
  initDataType: string;
  keySystem: string;
}

export interface StoredSessionData {
  sessionId: string;
  initData: string; // Base64 encoded
  initDataType: string;
  keySystem: string;
  timestamp: number;
}

interface LicenseCacheEntry {
  key: string;
  data: StoredSessionData[];
}

const PERSISTENT_LICENSE_PREFIX = 'motto_lic_';
const LICENSE_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours
const LICENSE_MAX_RETRY_ATTEMPTS = 10; // Maximum number of LRU evictions to attempt

/**
 * Gets all license cache keys from localStorage
 */
const getAllLicenseCacheKeys = (): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(PERSISTENT_LICENSE_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
};

/**
 * Gets all license cache entries
 */
const getAllLicenseCacheEntries = (): LicenseCacheEntry[] => {
  const keys = getAllLicenseCacheKeys();
  const entries: LicenseCacheEntry[] = [];
  
  for (const key of keys) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const data: StoredSessionData[] = JSON.parse(stored);
        entries.push({ key, data });
      }
    } catch (error) {
      console.warn(`Failed to parse license cache entry for key ${key}:`, error);
      // Remove corrupted entries
      localStorage.removeItem(key);
    }
  }
  
  return entries;
};

/**
 * Removes the least recently used license cache entry
 * @returns true if an entry was removed, false if no entries exist
 */
const evictLRUEntry = (): boolean => {
  const entries = getAllLicenseCacheEntries();
  
  if (entries.length === 0) {
    return false;
  }
  
  // Sort by oldest timestamp in each entry (oldest first)
  entries.sort((a, b) => {
    const oldestA = Math.min(...a.data.map(session => session.timestamp));
    const oldestB = Math.min(...b.data.map(session => session.timestamp));
    return oldestA - oldestB;
  });
  
  // Remove the oldest entry
  const lruEntry = entries[0];
  localStorage.removeItem(lruEntry.key);
  
  console.log(`Evicted LRU license cache entry: ${lruEntry.key}`);
  return true;
};

/**
 * Attempts to store data in localStorage with LRU eviction on quota exceeded
 */
const storeWithQuotaHandling = (key: string, data: string): boolean => {
  let attempts = 0;
  
  while (attempts < LICENSE_MAX_RETRY_ATTEMPTS) {
    try {
      localStorage.setItem(key, data);
      return true;
    } catch (error) {
      // Check if this is a quota exceeded error
      if (error instanceof DOMException && (
        error.code === 22 || // QUOTA_EXCEEDED_ERR
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      )) {
        console.warn(`QuotaExceededError on attempt ${attempts + 1}, attempting LRU eviction...`);
        
        // Try to evict an LRU entry
        if (!evictLRUEntry()) {
          console.error('No more entries to evict, storage operation failed');
          return false;
        }
        
        attempts++;
      } else {
        // Non-quota error, don't retry
        console.error('Failed to store license cache data:', error);
        return false;
      }
    }
  }
  
  console.error(`Failed to store license cache data after ${LICENSE_MAX_RETRY_ATTEMPTS} eviction attempts`);
  return false;
};

/**
 * Shared function to manage persistent license storage by:
 * 1. Loading existing sessions from localStorage
 * 2. Filtering out expired sessions
 * 3. Merging with new sessions (if provided)
 * 4. Updating localStorage with the cleaned data (with quota handling)
 */
export const managePersistentLicenseStorage = (
  playlistId: string,
  licenseCacheKey: string,
  newSessionMetadata?: PersistentSessionMetadata[]
): StoredSessionData[] => {
  try {
    const storageKey = `${PERSISTENT_LICENSE_PREFIX}${playlistId}_${licenseCacheKey}`;
    const stored = localStorage.getItem(storageKey);
    let existingSessions: StoredSessionData[] = [];

    if (stored) {
      try {
        existingSessions = JSON.parse(stored);
      } catch (parseError) {
        console.warn('Failed to parse existing persistent license data:', parseError);
        existingSessions = [];
      }
    }
    
    const now = Date.now();
    
    // Filter out expired sessions
    let validSessions = existingSessions.filter(session => 
      now - session.timestamp < LICENSE_EXPIRY_MS
    );
    
    // If new sessions are provided, merge them with existing valid sessions
    if (newSessionMetadata && newSessionMetadata.length > 0) {
      const newSessions: StoredSessionData[] = newSessionMetadata.map(session => {
        const uint8Array = new Uint8Array(session.initData);
        const binaryString = Array.from(uint8Array).map(byte => String.fromCharCode(byte)).join('');
        return {
          sessionId: session.sessionId,
          initData: btoa(binaryString),
          initDataType: session.initDataType,
          keySystem: session.keySystem,
          timestamp: now
        };
      });
      
      // Remove any existing sessions with the same sessionId to avoid duplicates
      const newSessionIds = new Set(newSessions.map(s => s.sessionId));
      validSessions = validSessions.filter(session => !newSessionIds.has(session.sessionId));
      
      // Add new sessions
      validSessions = [...validSessions, ...newSessions];
    }
    
    // Update localStorage with the cleaned/merged data using quota handling
    if (validSessions.length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      const dataToStore = JSON.stringify(validSessions);
      const success = storeWithQuotaHandling(storageKey, dataToStore);
      
      if (!success) {
        console.error(`Failed to store license cache for ${storageKey} after quota handling`);
        // Return the sessions even if storage failed
        return validSessions;
      }
    }
    
    return validSessions;
  } catch (error) {
    console.warn('Failed to manage persistent license storage:', error);
    return [];
  }
};

/**
 * Stores persistent license sessions for a playlist
 */
export const storePersistentLicense = (
  playlistId: string,
  licenseCacheKey: string,
  sessionMetadata: PersistentSessionMetadata[]
): void => {
  managePersistentLicenseStorage(playlistId, licenseCacheKey, sessionMetadata);
};

/**
 * Retrieves persistent license sessions for a playlist
 */
export const retrievePersistentLicense = (
  playlistId: string,
  licenseCacheKey: string
): PersistentSessionMetadata[] => {
  try {
    const validSessions = managePersistentLicenseStorage(playlistId, licenseCacheKey);
    
    return validSessions.map(session => ({
      sessionId: session.sessionId,
      initData: Uint8Array.from(atob(session.initData), c => c.charCodeAt(0)).buffer,
      initDataType: session.initDataType,
      keySystem: session.keySystem
    }));
  } catch (error) {
    console.warn('Failed to retrieve persistent license:', error);
    return [];
  }
};

/**
 * Clears all cached persistent licenses from localStorage
 * @returns The number of license cache entries that were cleared
 */
export const clearAllPersistentLicenses = (): number => {
  try {
    const keys = getAllLicenseCacheKeys();
    
    for (const key of keys) {
      localStorage.removeItem(key);
    }
    
    console.log(`Cleared ${keys.length} persistent license cache entries`);
    return keys.length;
  } catch (error) {
    console.error('Failed to clear persistent licenses:', error);
    return 0;
  }
};
